import type React from "react";
import { useContext } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { SmallBlueClock } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { Avatar } from "shared/atoms/Avatar";
import { Hidden } from "shared/atoms/Hidden";
import { HybridCareIcon } from "shared/atoms/HybridCareIcon";
import { PremiumIcon } from "shared/atoms/PremiumIcon";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";

import {
  AfterNameIcon,
  AvatarContainer,
  Name,
  NameContainer,
  PatientDetailsContainer,
  Priority,
  TopSection,
  isPatientVisible,
} from "../helpers";
import { useSelectedTreatment } from "../hooks/useSelectedTreatment";
import { PatientHeaderButtonRow } from "../PatientHeaderButtonRow";

import { InfoList } from "./InfoList";
import { PersonalDetails } from "./PersonalDetails";
import { TreatmentPane } from "./TreatmentPane";

interface Props {
  isCompactView?: boolean;
  toggleEndTreatment?: () => void;
}

export const PatientDataList: React.FC<Props> = ({ isCompactView = false, toggleEndTreatment }) => {
  const { profile } = useProfileContext();
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();

  invariant(profile);

  const selectedTreatment = useSelectedTreatment(patient?.treatments, previousTreatmentId);

  const formatDate = (date: string) => {
    // date can be null
    return date ? format(parseISO(date), "PP") : t("common.unknown");
  };

  if (!patient) return null;

  return (
    <>
      <Hidden type="aboveTablet">
        <PersonalDetails
          age={patient.age}
          gender={patient.gender}
          preferredName={patient.preferred_name}
          phoneNumber={patient.phone_number}
          country={patient.country}
        />
        <PatientDetailsContainer data-testid="patient-data-list-mobile">
          <InfoList />
          {selectedTreatment && (
            <TreatmentPane
              adherence={patient?.adherence}
              week={patient?.week}
              isCurrentTreatment={selectedTreatment.is_current_treatment}
              started={formatDate(selectedTreatment.created_at)}
            />
          )}
        </PatientDetailsContainer>
      </Hidden>

      <Hidden type="belowTablet">
        <div data-testid="patient-data-list-desktop">
          <PatientHeaderContainer>
            <PatientInfoColumn>
              <AvatarContainer>
                <Avatar
                  size={64}
                  fontSize={24}
                  id={patient.id}
                  name={`${patient.first_name} ${patient.last_name}`}
                  src={patient.avatars?.large}
                />
                <Priority data-testid="prio-icon" $hidden={!patient.prio} />
              </AvatarContainer>

              <NameContainer>
                <TopSection>
                  <Name>{`${patient.first_name} ${patient.last_name}`}</Name>

                  {patient.therapist_assignment_role === "substitute_therapist" && (
                    <AfterNameIcon>
                      <SmallBlueClock />
                    </AfterNameIcon>
                  )}

                  <HybridCareIcon ceStructure={patient.ce_structure} size="big" margin="0 0 0 8px" />

                  <PremiumIcon premiumType={patient.premium_type} size="big" margin="0 0 0 8px" />
                </TopSection>

                <PersonalDetails
                  age={patient.age}
                  gender={patient.gender}
                  preferredName={patient.preferred_name}
                  phoneNumber={patient.phone_number}
                  country={patient.country}
                />
              </NameContainer>
            </PatientInfoColumn>

            {isPatientVisible(patient) && toggleEndTreatment && (
              <ButtonsColumn>
                <PatientHeaderButtonRow toggleEndTreatment={toggleEndTreatment} />
              </ButtonsColumn>
            )}
          </PatientHeaderContainer>

          <PatientDetailsContainer className={clsx({ compact: isCompactView })}>
            <InfoList />
            {selectedTreatment && (
              <TreatmentPane
                adherence={patient?.adherence}
                week={patient?.week}
                isCurrentTreatment={selectedTreatment.is_current_treatment}
                started={formatDate(selectedTreatment.created_at)}
              />
            )}
          </PatientDetailsContainer>
        </div>
      </Hidden>
    </>
  );
};

const PatientHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin: 24px 24px 16px 24px;
  flex-wrap: wrap;

  @media (max-width: 1360px) {
    flex-direction: column;
  }
`;

const PatientInfoColumn = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 0 1 auto;
  min-width: 0;
`;

const ButtonsColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
`;
